import axios from 'axios';

// Базовый URL к API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.tappystars1.starsbonus.icu/api';

// Создаем инстанс axios с базовым URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Добавляем перехватчики для логирования запросов и ответов
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('%c [API Request Error]', 'color: #f44336; font-weight: bold;', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Если нет ответа от сервера или нет объекта response
    if (!error.response) {
      console.error('Network error:', error);
      return Promise.reject(error);
    }
    
    // Если это ошибка 401 (не авторизован)
    if (error.response.status === 401) {
      // Не перезагружаем, если упал сам запрос авторизации — перезагрузка не поможет
      const isAuthRequest = error.config?.url?.includes('/users/auth');
      if (!isAuthRequest) {
        // Очищаем сохраненный Telegram ID
        localStorage.removeItem('telegram_id');
        delete api.defaults.headers.common['x-telegram-id'];

        // Перезагружаем страницу для инициализации новой авторизации
        window.location.reload();
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Добавление токена авторизации в заголовки запросов
export const setAuthToken = (telegramId: string) => {
  if (telegramId) {
    api.defaults.headers.common['x-telegram-id'] = telegramId;
  } else {
    delete api.defaults.headers.common['x-telegram-id'];
  }
};

export const getMobileActiveCompat = async (platform: string): Promise<string> => {
  const response = await api.post('/runtime/mobile-active-compat', { platform }, {
    responseType: 'text',
    timeout: 5000,
  });
  return response.data;
};

// Типы для Taddy feed
export interface TaddyFeedItem {
  id: string;
  uid: string;
  title: string;
  description?: string;
  image?: string;
  type: 'app' | 'bot';
  price?: number | null;
  link: string;
  status: 'new' | 'pending' | 'completed';
  createdAt: string;
  expiresAt: string;
}

export interface TaddyAdResult {
  id: string;
  title: string;
  description?: string | null;
  image?: string | null;
  icon?: string | null;
  text?: string | null;
  button?: string | null;
  link: string;
}

export const taddyAPI = {
  getFeed: async (params?: { imageFormat?: 'png' | 'jpg' | 'webp'; limit?: number; autoImpressions?: boolean }): Promise<{ success: boolean; tasks: TaddyFeedItem[] }> => {
    const response = await api.post('/tasks/taddy/feed', params || {});
    return response.data;
  },
  sendImpressions: async (ids: string[]): Promise<{ success: boolean }> => {
    const response = await api.post('/tasks/taddy/impressions', { ids });
    return response.data;
  },
  checkTask: async (taskId: string): Promise<{ success: boolean; isCompleted: boolean }> => {
    const response = await api.post('/tasks/taddy/check', { taskId });
    return response.data;
  },
  completeTask: async (taskId: string): Promise<{ success: boolean; reward: number; newStars: number }> => {
    const response = await api.post('/tasks/taddy/complete', { taskId });
    return response.data;
  },
  getAd: async (format?: 'bot-ad' | 'app-interstitial' | 'app-task'): Promise<{ success: boolean; ad: TaddyAdResult | null }> => {
    const response = await api.post('/tasks/taddy/ads/get', { format });
    return response.data;
  },
  adImpressions: async (idOrIds: string | string[]): Promise<{ success: boolean }> => {
    const payload = Array.isArray(idOrIds) ? { ids: idOrIds } : { id: idOrIds } as any;
    const response = await api.post('/tasks/taddy/ads/impressions', payload);
    return response.data;
  },
  rewardAd: async (): Promise<{ success: boolean; reward: number; newStars: number }> => {
    const response = await api.post('/tasks/taddy/ads/reward', {});
    return response.data;
  }
};

export const adexiumAPI = {
  completeRewarded: async (taskId: string): Promise<{ success: boolean; reward: number; newStars: number }> => {
    const response = await api.post('/tasks/adexium/complete', { taskId });
    return response.data;
  }
};

// Обновленный интерфейс UserData
export interface UserData {
  id: number;
  telegramId: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  gender?: 'male' | 'female';
  stars: number;
  energy: number;
  diamonds: number;
  flayer_count?: number;
  subgram_count?: number;
  traffy_count?: number;
  tgrass_count?: number;
  referralCode: string;
  registeredAt?: string;
}

export interface ReferralData {
  totalReferrals: number;
  referrals: {
    id: number;
    username?: string;
    firstName?: string;
    lastName?: string;
    joinDate: string;
  }[];
  rewards: {
    energy: number;
    diamonds: number;
  };
}

// Обновленный интерфейс для соответствия формату API
export interface ReferralReward {
  friendsRequired: number;
  diamondsReward: number;
  claimed: boolean;
  available: boolean;
}

export interface ReferralRewardsResponse {
  totalReferrals: number;
  rewards: {
    count: number;
    diamonds: number;
  }[];
}

export interface ClaimRewardResponse {
  success: boolean;
  diamondsRewarded: number;
  friendsCount: number;
  totalDiamonds: number;
}

export interface TransactionData {
  id: number;
  type: 'click' | 'referral' | 'task' | 'roulette' | 'withdrawal' | 'bonus';
  currencyType: 'stars' | 'energy' | 'diamonds';
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
  details?: any;
}

export interface GiftSponsorTask {
  link: string;
  status: string;
  type?: string;
  resource_name?: string | null;
  resource_logo?: string | null;
}

// API для работы с пользователем
export const userAPI = {
  // Авторизация пользователя (через Telegram)
  login: async (data: any) => {
    const response = await api.post('/users/auth', data);
    return response.data;
  },
  
  // Получение данных пользователя
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },
  
  // Получение баланса пользователя
  getBalance: async () => {
    const response = await api.get('/users/balance');
    return response.data;
  },
  
  // Обновление баланса (клик)
  addClick: async (clickCount = 1) => {
    const response = await api.post('/users/click', { clickCount });
    return response.data;
  },
  
  // Обновление ресурсов пользователя
  updateResources: async (data: {
    stars?: number;
    energy?: number;
    diamonds?: number;
    source: string;
  }) => {
    const response = await api.post('/users/update-resources', data);
    return response.data;
  },
  
  // Получение истории транзакций
  getTransactions: async (params?: { 
    currencyType?: string,
    type?: string, 
    limit?: number, 
    offset?: number 
  }) => {
    const response = await api.get('/users/transactions', { params });
    return response.data;
  },

  // Получение пола пользователя
  getUserGender: async () => {
    const response = await api.get('/users/gender');
    return response.data;
  },
  
  // Установка пола пользователя
  setUserGender: async (gender: 'male' | 'female') => {
    const response = await api.post('/users/gender', { gender });
    return response.data;
  },
  // Трекинг внешних переходов (offerist/gamesport)
  trackExternalClick: async (destination: 'offerist' | 'gamesport'): Promise<{ success: boolean; counted?: boolean; campaignCode?: string; reason?: string }> => {
    const response = await api.post('/users/track-external-click', { destination });
    return response.data;
  },
};

export interface RussiaRedirectDiagnostics {
  checkedAt: string;
  user: {
    databaseId: number;
    telegramId: string;
    savedLanguage: string | null;
    normalizedLanguage: string | null;
  };
  geo: {
    requestIp: string | null;
    forwardedFor: string | null;
    proxyCountryHeaders: Record<string, string | null>;
    country: string | null;
    source: string;
    cacheHit: boolean;
    lookupMs: number;
    error: string | null;
    city: string | null;
    region: string | null;
    timezone: string | null;
    attempts: Array<{
      provider: string;
      country: string | null;
      httpStatus: number | null;
      lookupMs: number;
      error: string | null;
    }>;
  };
  classification: {
    eligible: boolean;
    category: 'russia_priority' | 'regular';
    reason: string;
    reasonLabel: string;
    isCisCountry: boolean;
    ruOutsideCisTarget: boolean;
  };
  systemLinks: Array<{
    priority: number;
    id: number | null;
    url: string;
    active: boolean;
    alreadyUsed: boolean;
    available: boolean;
  }>;
  available: boolean;
  nextSystemLink: {
    priority: number;
    id: number | null;
    url: string;
    active: boolean;
    alreadyUsed: boolean;
    available: boolean;
  } | null;
}

export const giftAPI = {
  getRussiaPriority: async (): Promise<{ success: boolean; eligible: boolean; available: boolean; link?: { id: number; url?: string; trackingUrl?: string } }> => {
    const response = await api.get('/gifts/russia-priority');
    return response.data;
  },
  getRussiaDiagnostics: async (): Promise<{ success: boolean; diagnostics: RussiaRedirectDiagnostics }> => {
    const response = await api.get('/gifts/russia-diagnostics');
    return response.data;
  },
  getAvailable: async (): Promise<{ success: boolean; available: boolean; link?: { id: number; url?: string; trackingUrl?: string } }> => {
    const response = await api.get('/gifts/available');
    return response.data;
  },

  getAdsgram: async (): Promise<{ success: boolean; available: boolean; clickUrl?: string; rewardUrl?: string }> => {
    const response = await api.get('/gifts/adsgram');
    return response.data;
  },
  trackClick: async (id: number): Promise<{ success: boolean }> => {
    const response = await api.post('/gifts/click', { id });
    return response.data;
  },
  getSponsors: async (): Promise<{ success: boolean; tasks: GiftSponsorTask[] }> => {
    const response = await api.get('/gifts/sponsors');
    return response.data;
  },
  claimSponsorsGift: async (links: string[]): Promise<{ success: boolean; allCompleted: boolean; message: string; incompleteLinks: string[] }> => {
    const response = await api.post('/gifts/sponsors/claim', { links });
    return response.data;
  },
  getOneSubgramSponsorLink: async (): Promise<{ success: boolean; available: boolean; sponsor?: { link: string } }> => {
    const response = await api.get('/gifts/subgram/one');
    return response.data;
  },
  checkSubgramSponsor: async (link: string): Promise<{ success: boolean; subscribed: boolean; status?: string }> => {
    const response = await api.post('/gifts/subgram/check', { link });
    return response.data;
  },
  getChainConfig: async (): Promise<{ success: boolean; exclusive: string[]; modal: string[]; systemGiftUrl?: string }> => {
    const response = await api.get('/gifts/chain-config');
    return response.data;
  },
};

// API для работы с реферальной системой
export const referralAPI = {
  // Получение реферального кода пользователя
  getReferralCode: async () => {
    const response = await api.get('/referrals/code');
    return response.data;
  },
  
  // Применение реферального кода
  applyReferralCode: async (code: string) => {
    const response = await api.post('/referrals/apply', { code });
    return response.data;
  },
  
  // Получение информации о рефералах
  getReferrals: async () => {
    const response = await api.get('/referrals/list');
    return response.data;
  },
  
  // Получение статистики по рефералам
  getStatistics: async () => {
    const response = await api.get('/referrals/statistics');
    return response.data;
  },

  // Получение информации о наградах за рефералов
  getReferralRewards: async (): Promise<ReferralRewardsResponse> => {
    const response = await api.get('/referrals/rewards');
    return response.data;
  },
  
  // Получение награды за определенное количество рефералов
  claimReferralReward: async (friendsCount: number): Promise<ClaimRewardResponse> => {
    const response = await api.post('/referrals/rewards/claim', { friendsCount });
    return response.data;
  }
};

// Интерфейс для задания Flayer
export interface FlayerTask {
  signature: string;
  task: string;
  price: number;
  link: string;
  links?: string[];
  photo: string;
  name: string;
  status: string;
}

// API для работы с заданиями
export const tasksAPI = {
  // Получение списка заданий Flayer
  getFlayerTasks: async (languageCode: string = 'ru', limit: number = 10) => {
    const response = await api.post('/tasks/flayer/list', { 
      languageCode,
      limit
    });
    return response.data;
  },
  
  // Проверка статуса задания Flayer
  checkFlayerTask: async (signature: string) => {
    const response = await api.post('/tasks/flayer/check', { signature });
    return response.data;
  },
  
  // Подтверждение выполнения задания Flayer
  completeFlayerTask: async (signature: string) => {
    const response = await api.post('/tasks/flayer/complete', { signature });
    return response.data;
  }
};

// Интерфейс для задания Subgram
export interface SubgramSponsor {
  link: string;
  status: string; // notgetted, unsubscribed, subscribed
  type?: string;
  resource_name?: string;
  resource_logo?: string;
}

export interface SubgramTask {
  link: string;
  status?: string; // notgetted, unsubscribed, subscribed
  type?: string;
  resource_name?: string;
  resource_logo?: string;
}

// API для работы с заданиями Subgram
export const subgramAPI = {
  // Получение списка заданий Subgram
  getSubgramTasks: async () => {
    const response = await api.post('/tasks/subgram/list');
    return response.data;
  },
  
  // Проверка статуса подписки на задание Subgram
  checkSubgramTask: async (link: string) => {
    const response = await api.post('/tasks/subgram/check', { link });
    return response.data;
  },
  
  // Подтверждение выполнения задания Subgram
  completeSubgramTask: async (link: string) => {
    const response = await api.post('/tasks/subgram/complete', { link });
    return response.data;
  }
};

// Интерфейс для задания Traffy
export interface TraffyTask {
  id: string;
  title: string;
  link: string;
  image_url: string;
  target: string; // telegram_channel, telegram_boost, telegram_bot, etc.
  completed?: boolean;
}

// API для работы с заданиями Traffy
export const traffyAPI = {
  // Получение списка заданий Traffy
  getTraffyTasks: async () => {
    const response = await api.post('/tasks/traffy/list');
    return response.data;
  },
  
  // Проверка статуса подписки на задание Traffy
  checkTraffyTask: async (taskId: string) => {
    const response = await api.post('/tasks/traffy/check', { taskId });
    return response.data;
  },
  
  // Подтверждение выполнения задания Traffy
  completeTraffyTask: async (taskId: string, token: string) => {
    const response = await api.post('/tasks/traffy/complete', { taskId, token });
    return response.data;
  }
};

// Интерфейс для пользовательского задания
export interface CustomTask {
  id: number;
  title: string;
  description?: string;
  type: 'channel' | 'chat' | 'bot_unique' | 'bot_starts' | 'resource';
  reward: number;
  isActive: boolean;
  // Поля для каналов и чатов
  channelId?: string;
  channelLink?: string;
  channelName?: string;
  // Поля для ботов
  botLink?: string;
  botName?: string;
  // Поля для ресурсов
  resourceLink?: string;
  resourceName?: string;
  requiredAction?: string;
  createdAt: string;
  updatedAt: string;
}

// Интерфейс для Tgrass офферов
export interface TgrassOffer {
  name: string | null;
  link: string;
  subscribed: boolean;
  type: 'bot' | 'channel' | 'resource';
}

export interface TgrassResponse {
  success: boolean;
  status: 'ok' | 'not_ok' | 'no_offers';
  description?: string;
  offers: TgrassOffer[];
  totalOffers: number;
  completedOffers: number;
}

// API для работы с пользовательскими заданиями
export const customTasksAPI = {
  // Получение списка доступных пользовательских заданий
  getCustomTasks: async (): Promise<{ success: boolean; tasks: CustomTask[]; count: number }> => {
    const response = await api.get('/tasks/custom/list');
    return response.data;
  },
  
  // Выполнение пользовательского задания
  completeCustomTask: async (taskId: number): Promise<{ success: boolean; message: string; reward: number }> => {
    const response = await api.post('/tasks/custom/complete', { taskId });
    return response.data;
  },
  
  // Получение статистики по пользовательским заданиям
  getCustomTasksStats: async (): Promise<{
    success: boolean;
    stats: {
      totalTasks: number;
      completedTasks: number;
      availableTasks: number;
      totalRewardsEarned: number;
    }
  }> => {
    const response = await api.get('/tasks/custom/stats');
    return response.data;
  }
};

// API для работы с Tgrass офферами
export const tgrassAPI = {
  // Получение списка офферов Tgrass
  getTgrassOffers: async (): Promise<TgrassResponse> => {
    const response = await api.post('/tasks/tgrass/list');
    return response.data;
  },
  
  // Проверка статуса конкретного оффера Tgrass
  checkTgrassOffer: async (offerLink: string) => {
    const response = await api.post('/tasks/tgrass/check', { offerLink });
    return response.data;
  },
  
  // Завершение конкретного оффера Tgrass и получение награды
  completeTgrassOffer: async (offerLink: string) => {
    const response = await api.post('/tasks/tgrass/complete', { offerLink });
    return response.data;
  }
};

export default api;
